import React, { useCallback, useEffect, useState } from 'react'
import { func } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
// ************ CONSTANTS *************
import {
  appRoutes,
  ModifyFlowType,
  defaultToVehicleOptionsList,
  shouldPreFillVehicleLengthOnVehicleSelectionChange
} from '../../configuration/constants'
import {
  tripTypes,
  vehicleSelectionDefaultState,
  vehicleTypes
} from '../../redux/vehicleSelections/constants'
import { crossingTypes } from '../../redux/crossings/constants'
// ************ ACTIONS *************
import {
  resetVehicleSelections,
  setVehicleDetails,
  setVehicleFormType,
  updateVehicleDetails
} from '../../redux/vehicleSelections/actions'
import * as UserSelectionActions from '../../redux/userSelections/actions'
import { fetchCrossing } from '../../redux/crossings/actions'
// *********** COMPONENTS ***********
import BrandLoader from '../../components/BrandLoader/BrandLoader'
import PageWarningBanner from '../../components/PageWarningBanner/PageWarningBanner'
import VehicleOptionList from '../../components/VehicleSelection/VehicleOptionList/VehicleOptionList'
// ********* SELECTORS **************
import {
  selectAvailableVehicleOptions,
  selectLabels,
  selectBicycleResourceCode
} from '../../redux/configuration/selectors'
import {
  selectActiveRouteForm,
  selectIsCommercial
} from '../../redux/ferryRouteSelections/selectors'
import {
  selectActiveModifyBooking,
  selectModifyFlowType
} from '../../redux/modifyBooking/selectors'
import { selectCrossingData } from '../../redux/crossings/selectors'
import { selectPaymentFerryId } from '../../redux/paymentConfirmation/selectors'
// import selectPassengerQuantitiesFetchCrossingsParams from '../../redux/selectors/selectPassengerQuantitiesFetchCrossingsParams'

// ********** GLOBAL SELECTORS ******
import selectInitialVehicleValues from '../../redux/selectors/selectInitialVehicleValues'
import selectVehicleFormType from '../../redux/selectors/selectVehicleFormType'
import selectRouteVehicleOptions from '../../redux/selectors/selectRouteVehicleOptions'
// *********** UTILITIES ************
import { mapNewlinesAsBreaks } from '../../utilities/rendering/renderUtils'
import { validateDirection } from '../../redux/vehicleSelections/utilities'
// *********** STYLES ***************
import '../../styles/scss/components/vehicles/passenger-vehicles.scss'
import '../../styles/scss/components/vehicles/passenger-vehicles-details-form.scss'
import '../../styles/scss/components/vehicles/passenger-vehicles-group-types-toggles.scss'
import '../../styles/scss/components/vehicles/passenger-vehicles-initial-form.scss'
import CommercialVehicle from '../../components/VehicleSelection/CommercialVehicle/CommercialVehicle'

/**
 * Vehicle Selection Container
 */
const VehicleSelectionContainer = ({ handleRouteChange }) => {
  const dispatch = useDispatch()
  const activeRouteForm = useSelector(selectActiveRouteForm)
  const initialValues = useSelector(selectInitialVehicleValues)
  const crossingData = useSelector(selectCrossingData)
  const ferryId = useSelector(selectPaymentFerryId)
  const labels = useSelector(selectLabels)
  const initialFormType = useSelector(selectVehicleFormType)
  const defaultVehicleOptions = useSelector(selectRouteVehicleOptions)
  const groupedVehicleOptions = useSelector(selectAvailableVehicleOptions)
  const bicycleResourceCode = useSelector(selectBicycleResourceCode)
  const activeModifyBooking = useSelector(selectActiveModifyBooking)
  const modifyFlowType = useSelector(selectModifyFlowType)
  const isCommercial = useSelector(selectIsCommercial)
  const lengthValidate = /^\d{1,2}(?:\.\d+)?$/
  const [length, setLength] = useState(
    initialValues?.[crossingTypes.DEPARTURE]?.vehicleLength ?? ''
  )

  const [formValues, setFormValues] = useState(initialValues)
  const [didVehiclePrefill, setDidVehiclePrefill] = useState(false)
  const [bringingAVehicleSelection, updateBringingAVehicleSelection] = useState(
    initialFormType
  )

  // Reset local and redux vehicle selection state
  const clearVehicleSelections = (newFormType = '') => {
    dispatch(setVehicleFormType(newFormType))
    dispatch(resetVehicleSelections())

    updateBringingAVehicleSelection(newFormType)

    const newFormValues = {
      departure:
        modifyFlowType === ModifyFlowType.DEPARTURE_LOCKED
          ? formValues.departure
          : { ...vehicleSelectionDefaultState },
      return: { ...vehicleSelectionDefaultState }
    }

    setFormValues(newFormValues)
  }

  const clearVehicleSelectionsMemoized = useCallback(clearVehicleSelections, [])

  const isWalkOn = bringingAVehicleSelection === vehicleTypes.VEHWLK
  const isBicycle = bringingAVehicleSelection === bicycleResourceCode

  const renderReturnVehicleForm =
    !defaultToVehicleOptionsList &&
    activeRouteForm !== tripTypes.OW &&
    bringingAVehicleSelection === vehicleTypes.VEHUNQ

  const vehicleOptions = defaultToVehicleOptionsList
    ? defaultVehicleOptions
    : groupedVehicleOptions
  // check vehicle option list
  const useVehicleOptionList =
    defaultToVehicleOptionsList && vehicleOptions?.vehicles

  useEffect(() => {
    dispatch(fetchCrossing())
    if (activeRouteForm === tripTypes.RT) {
      dispatch(fetchCrossing(crossingTypes.RETURN))
    }
  }, [dispatch, activeRouteForm]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!didVehiclePrefill && activeModifyBooking) {
      if (activeModifyBooking.departureVehicleDetails) {
        const {
          resourceCode,
          length,
          licenseNumber
        } = activeModifyBooking.departureVehicleDetails
        const vehicleType = vehicleOptions?.vehicles?.length
          ? vehicleOptions.vehicles.find(
              option => option.resourceCode === resourceCode
            )
          : null

        const lengthValue =
          +length % 1 === 0 ? (+length).toFixed() : String(length)
        const vehicleFormValues = {
          vehicleType,
          vehicleLength: lengthValue,
          vehicleLicense: licenseNumber
        }

        const formValuesUpdate = { ...formValues }

        formValuesUpdate[crossingTypes.DEPARTURE] = {
          ...formValuesUpdate[crossingTypes.DEPARTURE],
          ...vehicleFormValues
        }
        if (activeModifyBooking.returnVehicleDetails) {
          const {
            resourceCode,
            length,
            licenseNumber
          } = activeModifyBooking.returnVehicleDetails
          const vehicleType = vehicleOptions?.vehicles?.length
            ? vehicleOptions.vehicles.find(
                option => option.resourceCode === resourceCode
              )
            : null
          const returnLengthValue =
            +length % 1 === 0 ? (+length).toFixed() : String(length)
          const returnVehicleFormValues = {
            vehicleType,
            vehicleLength: returnLengthValue,
            vehicleLicense: licenseNumber
          }

          formValuesUpdate[crossingTypes.RETURN] = {
            ...formValuesUpdate[crossingTypes.RETURN],
            ...vehicleFormValues,
            ...returnVehicleFormValues
          }
        }
        setFormValues(formValuesUpdate)
        setDidVehiclePrefill(true)
      } else {
        clearVehicleSelectionsMemoized(vehicleTypes.VEHWLK)
        setDidVehiclePrefill(true)
      }
    }
  }, [
    activeModifyBooking,
    clearVehicleSelectionsMemoized,
    didVehiclePrefill,
    formValues,
    vehicleOptions
  ])

  const updateVehicleTypeAndHandleSelection = copyOrNot => vehicle => {
    const vehicleType =
      activeRouteForm === tripTypes.OW
        ? vehicleTypes.VEHOWY
        : vehicleTypes.VEHDUP
    updateBringingAVehicleSelection(vehicleType)

    handleVehicleSelection(crossingTypes.DEPARTURE, vehicle, copyOrNot)
  }

  const handleVehicleSelection = (
    direction,
    vehicle,
    copyIntoReturn = false
  ) => {
    const vehicleInModify = {
      [crossingTypes.DEPARTURE]: activeModifyBooking?.departureVehicleDetails,
      [crossingTypes.RETURN]: activeModifyBooking?.returnVehicleDetails
    }
    const vechicleResourceCode = vehicleInModify[direction]?.resourceCode
    const isModifiedVehicle = vehicle.resourceCode === vechicleResourceCode
    const vehicleLengthInModification = isModifiedVehicle
      ? vehicleInModify[direction]?.length
      : ''
    const vehicleUpdates = {
      vehicleType: vehicle,
      vehicleLength:
        +vehicle.defaultLength < 21 &&
        shouldPreFillVehicleLengthOnVehicleSelectionChange
          ? String(vehicle.defaultLength ?? '')
          : vehicleLengthInModification,
      vehicleLicense: '',
      vehicleHeight: vehicle.defaultHeight ?? 0
    }
    let updatedFormValues = {
      ...formValues,
      [direction]: {
        ...formValues[direction],
        ...vehicleUpdates
      }
    }

    if (copyIntoReturn) {
      updatedFormValues = {
        ...updatedFormValues,
        [crossingTypes.RETURN]: {
          ...updatedFormValues[crossingTypes.RETURN],
          ...vehicleUpdates
        }
      }
    }

    setFormValues(updatedFormValues)
  } // end of handleVehicleSelection

  const renderContinueDisabled = () => {
    if (isWalkOn || isBicycle) return false

    if (
      isCommercial &&
      (!lengthValidate.test(String(length)) || parseFloat(length) === 0)
    ) {
      return true
    }
    if (defaultToVehicleOptionsList) {
      if (
        formValues.departure &&
        Object.keys(formValues.departure).length > 0 &&
        formValues.departure.vehicleType &&
        Object.keys(formValues.departure.vehicleType).length > 0
      ) {
        return false
      }
    }
    if (!bringingAVehicleSelection) return true

    let isValid = validateDirection(formValues.departure)

    if (renderReturnVehicleForm && isValid) {
      isValid = validateDirection(formValues.return)
    }

    return !isValid
  }

  const submit = () => {
    // START new selection flow

    // TODO(ebarrett):
    //  - Verify it's working for CTMA
    //  - Verify this on modify flow
    //  - renderReturnVehicleForm logic is convoluted... Maybe clean up?
    //  - Would be nice if these were dispatched on selection instead of submit
    const { departure: departureValues, return: returnValues } = formValues

    dispatch(
      UserSelectionActions.setDepartureVehicle({
        resourceCode: departureValues.vehicleType.resourceCode,
        length:
          departureValues.vehicleLength ??
          departureValues.vehicleType.defaultLength,
        height:
          departureValues.vehicleHeight ??
          departureValues.vehicleType.defaultHeight,
        license: departureValues.vehicleLicense
      })
    )
    if (renderReturnVehicleForm) {
      dispatch(
        UserSelectionActions.setReturnVehicle({
          resourceCode: returnValues.vehicleType.resourceCode,
          length:
            returnValues.vehicleLength ??
            returnValues.vehicleType.defaultLength,
          height:
            returnValues.vehicleHeight ??
            returnValues.vehicleType.defaultHeight,
          license: returnValues.vehicleLicense
        })
      )
    } else {
      dispatch(UserSelectionActions.setIsSameVehicle(true))
    }
    // END new selection flow

    dispatch(setVehicleFormType(bringingAVehicleSelection))

    if (!isWalkOn) {
      const formattedDepartureValues = { ...formValues.departure }
      if (
        !formattedDepartureValues.vehicleLength &&
        formattedDepartureValues.vehicleType.defaultLength
      ) {
        formattedDepartureValues.vehicleLength = formattedDepartureValues.vehicleType.defaultLength.toString()
      }
      // set DEPARTURE vehicle
      dispatch(
        updateVehicleDetails({
          vehicleDetails: formattedDepartureValues,
          crossingType: crossingTypes.DEPARTURE
        })
      )
      // set RETURN vehicle in nfl
      if (defaultToVehicleOptionsList) {
        dispatch(
          updateVehicleDetails({
            vehicleDetails: formattedDepartureValues,
            crossingType: crossingTypes.RETURN
          })
        )
      }

      if (renderReturnVehicleForm) {
        const formattedReturnValues = { ...formValues.return }
        if (
          !formattedReturnValues.vehicleLength &&
          formattedReturnValues.vehicleType.defaultLength
        ) {
          formattedReturnValues.vehicleLength = formattedReturnValues.vehicleType.defaultLength.toString()
        }

        dispatch(
          updateVehicleDetails({
            vehicleDetails: formattedReturnValues,
            crossingType: crossingTypes.RETURN
          })
        )
      } else if (!defaultToVehicleOptionsList) {
        // Reset return vehicle
        dispatch(
          setVehicleDetails({
            crossingType: crossingTypes.RETURN,
            vehicle: { ...initialValues.return }
          })
        )
      }
    } else {
      // WALK ON, we'll clear any previously made selections.
      dispatch(
        setVehicleDetails({
          crossingType: crossingTypes.DEPARTURE,
          vehicle: { ...vehicleSelectionDefaultState }
        })
      )
      dispatch(
        setVehicleDetails({
          crossingType: crossingTypes.RETURN,
          vehicle: { ...vehicleSelectionDefaultState }
        })
      )
    }

    if (modifyFlowType === ModifyFlowType.DEPARTURE_LOCKED) {
      handleRouteChange(appRoutes.return.pathname)
    } else {
      handleRouteChange(appRoutes.departure.pathname)
    }
  } // end of submit

  const isLoading = Boolean(
    crossingData?.departure?.loading || crossingData?.return?.loading
  )

  const vehicleMessageBanner = ferryId
    ? labels[`vehicleMessageBanner${ferryId.toUpperCase()}`]
    : null

  const vehicleDisclaimerBanner = ferryId
    ? labels[`vehicleDisclaimer${ferryId.toUpperCase()}`]
    : null

  const renderWarningBanner = label => {
    if (!label) {
      return null
    }
    return (
      <PageWarningBanner
        message={label}
        centered={process.env.REACT_APP_FLOW_CLIENT_ID === 'nfl'}
      >
        <p className="u-remove-margin u-text-center">
          {mapNewlinesAsBreaks(label)}
        </p>
      </PageWarningBanner>
    )
  }

  return (
    <div className="passenger-vehicles u-page-view-container">
      <div className="u-container">
        {' '}
        <div className="u-container">
          {isLoading ? (
            <BrandLoader />
          ) : isCommercial ? (
            <div className="passenger-vehicles__content">
              <h2 className="theme-font-header">
                {labels?.commercialVehicleDetails}
              </h2>
              <p className="u-text-center passenger-vehicles__content-text">
                {labels?.lengthOverX}
              </p>
              {useVehicleOptionList && (
                <CommercialVehicle
                  isLoading={isLoading}
                  handleVehicleSelection={updateVehicleTypeAndHandleSelection(
                    activeRouteForm === tripTypes.OW
                  )}
                  vehicleOptions={vehicleOptions?.vehicles[0]}
                  lengthValidate={lengthValidate}
                  length={length}
                  setLength={setLength}
                />
              )}
            </div>
          ) : (
            <div className="passenger-vehicles__content">
              <h2 className="theme-font-header">{labels.bringingAVehicle}</h2>

              {renderWarningBanner(vehicleMessageBanner)}
              {renderWarningBanner(vehicleDisclaimerBanner)}

              {/* for NFL client currently */}
              {useVehicleOptionList && (
                <VehicleOptionList
                  labels={labels}
                  isLoading={isLoading}
                  handleVehicleSelection={updateVehicleTypeAndHandleSelection(
                    activeRouteForm === tripTypes.OW
                  )}
                  formValues={formValues[crossingTypes.DEPARTURE]}
                  vehicleOptions={vehicleOptions?.vehicles}
                  bestRates={
                    crossingData?.[crossingTypes.DEPARTURE]?.resourceBestRates
                  }
                  selectIsWalkOn={() => {
                    clearVehicleSelections(vehicleTypes.VEHWLK)
                  }}
                  isWalkOn={isWalkOn}
                  allowWalkOn
                />
              )}
            </div>
          )}

          <div className="passenger-vehicles__buttons u-page-submit-container">
            {!isLoading && (
              <button
                disabled={renderContinueDisabled()}
                className="btn btn-primary large-primary-btn"
                onClick={() => submit()}
              >
                {labels.continueToSailingsBtn} →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

VehicleSelectionContainer.propTypes = {
  handleRouteChange: func.isRequired
}

export default VehicleSelectionContainer
